// Module 19 (BL-380) G5 - check-in words and numbers, shared by the Guests screen, the door scanner and the board. Pure functions only.
import { guardCsvText } from '../eventsBoard';
import { rsvpLabel } from '../eventLabels';

export const DOOR_SCREEN_LABEL = 'Door check-in';

// "7:42 PM" (in `zone` when given, else the device's own clock: door staff stand at the venue).
export function formatClock(value, zone) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', ...(zone ? { timeZone: zone } : {}) }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
    }
}

// "Oct 24, 2026 7:42 PM" for the report.
export function formatStamp(value, zone) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try {
        return new Intl.DateTimeFormat('en-US', { ...options, ...(zone ? { timeZone: zone } : {}) }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-US', options).format(date);
    }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// What the door scanner shows after a scan. tone: 'in' (green), 'repeat' (gold), 'refused' (red).
export function scanResultView(result, zone) {
    if (!result) return null;
    if (result.ok === false) {
        return { tone: 'refused', title: 'Not checked in', detail: result.message || 'This pass cannot be used here.', code: result.code || 'refused' };
    }
    const repeat = result.repeat === true;
    const title = repeat ? `Already checked in at ${formatClock(result.checkedInAt, zone)}` : 'Checked in';
    const lines = [];
    if (result.isPlusOne) lines.push(`Plus-one of ${result.hostName || 'a guest'}`);
    const plusOnes = Number(result.plusOnes) || 0;
    const plusIn = Number(result.plusOnesCheckedIn) || 0;
    if (!result.isPlusOne && plusOnes > 0) lines.push(`Party of ${plusOnes + 1} · ${plusIn} of ${plural(plusOnes, 'plus-one', 'plus-ones')} in`);
    if (result.rsvp && result.rsvp !== 'accepted') lines.push(`RSVP: ${rsvpLabel(result.rsvp)}`);
    if (Number(result.plusOnesAdded) > 0) lines.push(`${plural(Number(result.plusOnesAdded), 'plus-one', 'plus-ones')} checked in too`);
    return {
        tone: repeat ? 'repeat' : 'in',
        title,
        name: result.name || '',
        detail: lines.join(' · '),
        plusOnesLeft: result.isPlusOne ? 0 : Math.max(plusOnes - plusIn, 0),
    };
}

// The "Checked in" tile on the Guests screen and the line on the board card. `numbers` comes from m19_event_attendance().
export function attendanceSummary(numbers) {
    if (!numbers) return null;
    const n = (key) => Number(numbers[key]) || 0;
    const parts = [`${n('checked_in_expected')} of ${n('expected')} expected`];
    if (n('walk_ins') > 0) parts.push(plural(n('walk_ins'), 'walk-in', 'walk-ins'));
    if (numbers.is_over && numbers.no_shows !== null && numbers.no_shows !== undefined) parts.push(plural(n('no_shows'), 'no-show', 'no-shows'));
    return {
        checkedIn: n('checked_in'),
        expected: n('expected'),
        note: parts.join(' · '),
        isOver: numbers.is_over === true,
        noShows: numbers.is_over ? n('no_shows') : null,
        text: `${n('checked_in')} checked in · ${parts.join(' · ')}`,
    };
}

// The Guests screen's check-in filter: 'all' | 'in' | 'out'.
export function filterByCheckin(guests, checkins, filter) {
    if (!checkins || filter === 'all' || !filter) return guests;
    return guests.filter((guest) => (filter === 'in') === checkins.has(guest.id));
}

export const REPORT_COLUMNS = ['Guest', 'Plus-one of', 'Group', 'RSVP', 'Expected', 'Attendance', 'Checked in at', 'Checked in by', 'How'];
export const STATUS_WORDS = {
    checked_in: 'Checked in',
    walk_in: 'Walk-in (checked in without a Yes)',
    no_show: 'No-show',
    not_yet: 'Not checked in yet',
    not_expected: 'Not expected',
};
const METHOD_WORDS = { qr: 'QR pass', manual: 'From the list' };

function csvCell(value) {
    const text = guardCsvText(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// The attendance report (m19_attendance_report()) as a spreadsheet file. Times in the company's time zone.
export function attendanceCsv(report) {
    const rows = (report?.rows || []).map((row) => [
        row.name || '', row.hostName || '', row.group || '', rsvpLabel(row.rsvp), row.expected ? 'Yes' : 'No',
        STATUS_WORDS[row.status] || row.status || '', formatStamp(row.checkedInAt, report?.timeZone), row.checkedInBy || '',
        METHOD_WORDS[row.method] || '',
    ]);
    return `﻿${[REPORT_COLUMNS, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

export function attendanceFileName(report) {
    const slug = String(report?.eventName || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'event';
    return `attendance-${slug}${report?.eventDate ? `-${report.eventDate}` : ''}.csv`;
}

// The file name for one guest's pass download.
export function passFileName(name, extension = 'png') {
    const slug = String(name || 'guest').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'guest';
    return `pass-${slug}.${extension}`;
}
